import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { extend, Extent, buffer, createEmpty } from "ol/extent";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature, Map } from "ol";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Coordinate } from "ol/coordinate";
import { getRouteSymbol } from "./symbols";
import {
    IMammadabaMapProps,
    OnFeatureClickCallback,
    OnUnselectCallback,
    OnRouteClickCallback,
    OnRouteUnselectCallback,
    IPadding,
    OnGameSelectedCallback,
    OnCheckPointSelectedCallback
} from "./mapProvider";
import { FeatureType, RouteLayers, RoutePointDataSource, SelectedStatus } from "./enums";
import { config } from "./config";
import { useAppContext } from "./AppContext";
import {
    addGameLayers,
    addRouteLayers,
    addTracks,
    changeLayerVisibility,
    createMap,
    forEachFeatureOfLayer,
    getLayerById,
    getNearbyCircle,
    getSelectRouteFeature,
    handleClusterClick,
    onPointerMove,
    selectOtherSourceRoutePoint,
    selectTrack,
    setHoverStyle,
    showTrackPoints,
    unselectLVMRoutePoint,
    unselectRoutePoint,
    zoomToExtent,
    zoomToFeature
} from "./mapFunctions";
import {
    finishGame,
    selectGame,
    selectGameCheckPoint,
    selectGameCheckPointUsingId,
    selectGameUsingId,
    setCheckPointFound,
    startGame
} from "./gameFunctions";
import { FilterOptions } from "./filters";

const centerPoint = new Point([config.defaults.center[0], config.defaults.center[1]]);
const _padding: IPadding = { left: 0, duration: 0 };
const clusterLayerId = config.defaults.clusterLayerId;
const selectedRouteLayerId = config.defaults.selectedRouteLayerId;
const tracksLayerId = config.defaults.tracksLayerId;

const getBuffer = (feature: Feature) => buffer(feature.getGeometry().getExtent(), 100);

let currentPosition = undefined;
/**
 * @returns current position, as [longitude,latitude]
 */
export const getCurrentPosition = (): number[] | undefined => {
    return currentPosition;
};

let _map: MutableRefObject<Map>;
let allFeaturesRef: MutableRefObject<Feature[]>;
let sourceRef: MutableRefObject<VectorSource>;
let selectedSourceRef: MutableRefObject<VectorSource>;
let routesSourceRef: MutableRefObject<VectorSource>;
let filterOptionsRef: MutableRefObject<FilterOptions>;

let onFeatureClick: MutableRefObject<OnFeatureClickCallback>;
let onUnselect: MutableRefObject<OnUnselectCallback>;
let onRouteUnselect: MutableRefObject<OnRouteUnselectCallback>;
let onGameSelected: MutableRefObject<OnGameSelectedCallback>;
let onCheckPointSelected: MutableRefObject<OnCheckPointSelectedCallback>;

export const useMap = (target: MutableRefObject<HTMLDivElement>, props: IMammadabaMapProps): Map => {
    _map = useRef<Map>(null);
    sourceRef = useRef<VectorSource>(new VectorSource());
    selectedSourceRef = useRef<VectorSource>(new VectorSource());
    routesSourceRef = useRef<VectorSource>(new VectorSource());
    allFeaturesRef = useRef<Feature[]>();
    filterOptionsRef = useRef<FilterOptions>(props.filterOptions);

    onFeatureClick = useRef<OnFeatureClickCallback>(props.onClickOnFeature);
    onFeatureClick.current = useMemo(() => props.onClickOnFeature, [props.onClickOnFeature]);
    onUnselect = useRef<OnUnselectCallback>(props.onUnselect);
    onUnselect.current = useMemo(() => props.onUnselect, [props.onUnselect]);

    const onRouteClick = useRef<OnRouteClickCallback>(props.onRouteSelect);
    onRouteClick.current = useMemo(() => props.onRouteSelect, [props.onRouteSelect]);
    onRouteUnselect = useRef<OnRouteUnselectCallback>(props.onRouteUnselect);
    onRouteUnselect.current = useMemo(() => props.onRouteUnselect, [props.onRouteUnselect]);

    onGameSelected = useRef<OnGameSelectedCallback>(props.onGameSelected);
    onGameSelected.current = useMemo(() => props.onGameSelected, [props.onGameSelected]);

    onCheckPointSelected = useRef<OnCheckPointSelectedCallback>(props.onCheckPointSelected);
    onCheckPointSelected.current = useMemo(() => props.onCheckPointSelected, [props.onCheckPointSelected]);

    const nearbyCircle = useRef<Feature>();
    const nearbyCircleLayer = useRef<VectorLayer>();

    const { texts } = useAppContext();

    useEffect(() => {
        if (target.current) {
            _map.current = createMap(target.current, sourceRef.current, texts);

            _map.current.on("pointermove", (evt) => onPointerMove(evt, _map.current, target.current));

            _map.current.on("singleclick", (evt) => {
                if (evt.dragging) {
                    return;
                }
                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel, {
                    layerFilter: (layer) => {
                        const layerId = layer.get("id");
                        return (
                            layerId === RouteLayers.Route ||
                            layerId === RouteLayers.RoutePoints ||
                            layerId === clusterLayerId ||
                            layerId === selectedRouteLayerId ||
                            layerId === FeatureType.GameMainPoint ||
                            layerId === FeatureType.GameCheckPoint
                        );
                    }
                });

                if (hasFeatureAtPixel) {
                    const selectedRouteFeatures = _map.current.getFeaturesAtPixel(evt.pixel, {
                        layerFilter: (layer) =>
                            layer.get("id") === RouteLayers.RoutePoints || layer.get("id") === selectedRouteLayerId
                    });
                    if (selectedRouteFeatures.length) {
                        handleRoutePointClick(selectedRouteFeatures[0]);
                    } else {
                        _map.current.forEachFeatureAtPixel(evt.pixel, handleRouteClick, {
                            layerFilter: (layer) => layer.get("id") === RouteLayers.Route
                        });
                        _map.current.forEachFeatureAtPixel(evt.pixel, handleObjectClick, {
                            layerFilter: (layer) => layer.get("id") === clusterLayerId
                        });
                        _map.current.forEachFeatureAtPixel(evt.pixel, handleGameObjectClick, {
                            layerFilter: (layer) => layer.get("id") === FeatureType.GameMainPoint
                        });
                        _map.current.forEachFeatureAtPixel(evt.pixel, handleGameCheckPointClick, {
                            layerFilter: (layer) => layer.get("id") === FeatureType.GameCheckPoint
                        });
                    }
                } else {
                    resetSelectState();
                }
            });

            _map.current.on("dblclick", (evt) => {
                if (evt.dragging) {
                    return;
                }
                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel, {
                    layerFilter: (layer) => {
                        const layerId = layer.get("id");
                        return (
                            layerId === RouteLayers.RoutePoints ||
                            layerId === clusterLayerId ||
                            layerId === selectedRouteLayerId
                        );
                    }
                });

                if (hasFeatureAtPixel) {
                    const selectedRouteFeatures = _map.current.getFeaturesAtPixel(evt.pixel, {
                        layerFilter: (layer) =>
                            layer.get("id") === RouteLayers.RoutePoints || layer.get("id") === selectedRouteLayerId
                    });
                    if (selectedRouteFeatures.length) {
                        evt.stopPropagation();
                        handleDoubleClick(selectedRouteFeatures[0]);
                    } else {
                        evt.stopPropagation();
                        _map.current.forEachFeatureAtPixel(evt.pixel, handleDoubleClick, {
                            layerFilter: (layer) => layer.get("id") === clusterLayerId
                        });
                    }
                } else {
                    resetSelectState();
                }
            });

            _map.current.on("moveend", () => document.dispatchEvent(new CustomEvent("moveend")));
            _map.current.on("movestart", () => document.dispatchEvent(new CustomEvent("movestart")));
            // @ts-ignore
            window.MAP = _map;

            window.onresize = () =>
                setTimeout(function () {
                    _map.current.updateSize();
                }, 200);

            window.visualViewport.onresize = () =>
                setTimeout(function () {
                    _map.current.updateSize();
                }, 300);

            const handleRouteClick = (feature: Feature) => {
                const clusteredFeatures: Feature[] = feature.get("features");
                if (clusteredFeatures.length) {
                    if (clusteredFeatures.length === 1) {
                        if (clusteredFeatures[0].get("SELECTED") === SelectedStatus.Default) {
                            resetSelectState();
                            clusteredFeatures[0].set("SELECTED", SelectedStatus.Selected);
                            setRouteTrackSelected(clusteredFeatures[0].get("ROUTEID"), clusteredFeatures[0]);
                        } else {
                            resetSelectState();
                        }
                        onRouteClick.current?.(clusteredFeatures[0]);
                    } else {
                        const extent = clusteredFeatures.reduce<Extent>((result, feature) => {
                            return result === null ? getBuffer(feature) : extend(result, getBuffer(feature));
                        }, null);
                        const view = _map.current.getView();
                        view.fit(extent, { padding: [0, 0, 0, view.paddingLeft], duration: 500 });
                    }
                } else {
                    resetSelectState();
                }
            };

            const handleObjectClick = (feature: Feature) => {
                const clusteredFeatures: Feature[] = feature.get("features");
                if (clusteredFeatures.length) {
                    if (clusteredFeatures.length === 1) {
                        if (clusteredFeatures[0].get("SELECTED") === SelectedStatus.Default) {
                            resetSelectState();
                            clusteredFeatures[0].set("SELECTED", SelectedStatus.Selected);
                            selectTrack(clusteredFeatures[0].get("OBJECTID"));
                        } else {
                            // resetSelectState();
                            const view = _map.current.getView();
                            view.fit(getBuffer(clusteredFeatures[0]), {
                                padding: [0, 0, 0, view.paddingLeft],
                                duration: 500
                            });
                        }
                        onFeatureClick.current?.(clusteredFeatures[0]);
                    } else {
                        handleClusterClick(clusteredFeatures);
                    }
                } else {
                    resetSelectState();
                }
            };

            const handleDoubleClick = (feature: Feature) => {
                if (feature.get("features").length !== 1) {
                    handleClusterClick(feature.get("features"));
                    return;
                }
                const view = _map.current.getView();
                view.fit(getBuffer(feature), {
                    padding: [0, 0, 0, view.paddingLeft],
                    duration: 500
                });
            };

            const handleGameObjectClick = (feature: Feature) => selectGame(feature, onGameSelected.current);
            const handleGameCheckPointClick = (feature: Feature) =>
                selectGameCheckPoint(feature, onCheckPointSelected.current);

            const handleRoutePointClick = (feature: Feature) => selectRoutePoint(feature);
        }
        document.addEventListener("currentPosition", (e: CustomEvent) => (currentPosition = e.detail));
    }, []);

    useEffect(() => {
        sourceRef.current.clear();
        props.allFeatures && sourceRef.current.addFeatures(props.allFeatures);
        allFeaturesRef.current = props.allFeatures;
        props.allFeatures && addTracks();
    }, [props.allFeatures]);

    useEffect(() => {
        if (props.features && props.features.length) {
            sourceRef.current.clear();
            sourceRef.current.addFeatures(props.features);
        } else {
            sourceRef.current.clear();
        }
    }, [props.features]);

    useEffect(() => {
        _map.current &&
            props.routeLayers &&
            props.routeLayers.length &&
            addRouteLayers(props.routeLayers, routesSourceRef.current, selectedSourceRef.current);
    }, [props.routeLayers]);

    useEffect(() => {
        if (_map.current && props.filteredRoutes && props.filteredRoutes.length) {
            routesSourceRef.current.clear();
            routesSourceRef.current.addFeatures(props.filteredRoutes);
        } else {
            routesSourceRef.current.clear();
        }
    }, [props.filteredRoutes]);

    useEffect(() => {
        _map.current && props.gameLayers && props.gameLayers.length && addGameLayers(props.gameLayers);
    }, [props.gameLayers]);

    useEffect(() => {
        props.zoomTo && zoomToFeature(props.zoomTo);
    }, [props.zoomTo]);

    useEffect(() => {
        changeLayerVisibility(props.layerVisibility);
    }, [props.layerVisibility]);

    useEffect(() => {
        props.nearbyCircleVisible
            ? addNearbyCircle(nearbyCircle, nearbyCircleLayer)
            : removeNearbyCircle(nearbyCircle, nearbyCircleLayer);
    }, [props.nearbyCircleVisible]);

    useEffect(() => {
        if (!props.selectRouteById || props.selectRouteById.routeId === undefined) {
            return;
        }
        const routeId = props.selectRouteById.routeId;
        if (routeId === "") {
            resetSelectState();
            return;
        }
        const routeFeature = getSelectRouteFeature(routeId);
        if (routeFeature) {
            resetSelectState();
            routeFeature.set("SELECTED", SelectedStatus.Selected);
            setHoverStyle(routeId, SelectedStatus.Selected);
            setRouteTrackSelected(routeId, routeFeature);
            onRouteClick.current?.(routeFeature);
        }
    }, [props.selectRouteById]);

    useEffect(() => {
        selectGameUsingId(props.selectGameById, onGameSelected.current);
    }, [props.selectGameById]);

    useEffect(() => {
        startGame(props.startGame);
    }, [props.startGame]);

    useEffect(() => {
        props.gameCompleted && finishGame(props.gameCompleted);
    }, [props.gameCompleted]);

    useEffect(() => {
        props.checkPointFound && setCheckPointFound(props.checkPointFound);
    }, [props.checkPointFound]);

    useEffect(() => {
        props.selectGameCheckPointById &&
            selectGameCheckPointUsingId(props.selectGameCheckPointById, onCheckPointSelected.current);
    }, [props.selectGameCheckPointById]);

    useEffect(() => {
        filterOptionsRef.current = props.filterOptions;
    }, [props.filterOptions]);

    useEffect(() => {
        if (!props.selectFeatureById) {
            return;
        }
        resetSelectState();
        const feature =
            props.selectFeatureById.layerId === clusterLayerId
                ? allFeaturesRef.current.find((f) => f.get("OBJECTID") === Number(props.selectFeatureById.objectId))
                : getLayerById(props.selectFeatureById.layerId)
                      .getSource()
                      .getFeatures()
                      .find((f: Feature) => {
                          if (f.get("OBJECTID") === Number(props.selectFeatureById.objectId)) {
                              f.set("ROUTE", 1);
                              return f;
                          } else {
                              f.set("ROUTE", 0);
                          }
                      });

        zoomToFeature(feature);
        selectRoutePoint(feature);
    }, [props.selectFeatureById]);

    useEffect(() => {
        _padding.left =
            props.padding.left === 0 && _padding.left > 0
                ? -_padding.left
                : props.padding.left > 0
                ? props.padding.left
                : 0;
        const view = _map.current.getView();
        const maxZoom = view.getZoom();
        const center = view.getCenter();
        centerPoint.setCoordinates(center);
        view.fit(centerPoint, {
            padding: [0, 0, 0, _padding.left],
            maxZoom,
            duration: props.padding.duration,
            callback: () => {
                view.paddingLeft = _padding.left < 0 ? 0 : _padding.left;
            }
        });
    }, [props.padding.left, props.padding.duration]);

    return _map.current;
};

const addNearbyCircle = async (
    nearbyCircle: MutableRefObject<Feature>,
    nearbyCircleLayer: MutableRefObject<VectorLayer>
) => {
    if (nearbyCircleLayer.current) {
        return;
    }
    const location = await getLocation();

    nearbyCircle.current = getNearbyCircle(location);
    nearbyCircleLayer.current = new VectorLayer({
        map: _map.current,
        source: new VectorSource({
            features: [nearbyCircle.current]
        })
    });
};

const removeNearbyCircle = (
    nearbyCircle: MutableRefObject<Feature>,
    nearbyCircleLayer: MutableRefObject<VectorLayer>
) => {
    nearbyCircleLayer.current && nearbyCircleLayer.current.getSource().clear();
    _map.current.removeLayer(nearbyCircleLayer.current);
    nearbyCircleLayer.current = null;
    nearbyCircle.current = null;
};

const getLocation = async (): Promise<Coordinate | undefined> => {
    return new Promise((resolve) => {
        currentPosition && currentPosition.length
            ? resolve(currentPosition)
            : navigator.geolocation.getCurrentPosition(
                  (e) => resolve((currentPosition = fromLonLat([e.coords.longitude, e.coords.latitude]))),
                  () => resolve(undefined)
              );
    });
};

const selectRoutePoint = (feature: Feature) => {
    if (feature.get("SELECTED") === SelectedStatus.Selected) {
        const view = _map.current.getView();
        view.fit(getBuffer(feature), { padding: [0, 0, 0, view.paddingLeft], duration: 500 });
    }
    unselectRoutePoint(onUnselect.current);
    unselectLVMRoutePoint(selectedSourceRef.current.getFeatures(), onUnselect.current);
    feature.set("SELECTED", SelectedStatus.Selected);

    // for non-LVM features, get attributes from layer SDEOWNER.MammadabaOtherPlaces  feature
    feature.get("TYPE") === RoutePointDataSource.Other && selectOtherSourceRoutePoint(feature);

    onFeatureClick.current?.(feature);
};

const resetSelectState = () => {
    // reset Route features
    const layers = _map.current.getLayers().getArray();
    for (const layer of layers) {
        if (layer.get("id") === RouteLayers.Route) {
            layer.getSource().forEachFeature((feature: Feature) => {
                const clusteredFeatures = feature.get("features");
                if (clusteredFeatures.length) {
                    for (const clusterFeature of clusteredFeatures) {
                        if (clusterFeature.get("SELECTED") !== SelectedStatus.Default) {
                            clusterFeature.set("SELECTED", SelectedStatus.Default);
                            onRouteUnselect.current?.(clusterFeature.get("ROUTEID"));
                        }
                    }
                }
            });
        }
        if (layer.get("id") === RouteLayers.RouteTrack) {
            layer.getSource().forEachFeature((feature: Feature) => {
                if (feature.get("SELECTED") !== SelectedStatus.Default) {
                    feature.set("SELECTED", SelectedStatus.Default);
                    feature.setStyle(getRouteSymbol(RouteLayers.RouteTrack, SelectedStatus.Default));
                }
            });
        }
        if (layer.get("id") === RouteLayers.RoutePoints) {
            layer.getSource().forEachFeature((feature: Feature) => {
                feature.get("SELECTED") !== SelectedStatus.Default && feature.set("SELECTED", SelectedStatus.Default);
                feature.get("ROUTE") !== 0 && feature.set("ROUTE", 0);
            });
        }
        if (layer.get("id") === FeatureType.GameCheckPoint) {
            forEachFeatureOfLayer(FeatureType.GameCheckPoint, (f: Feature) => {
                f.get("SELECTED") && onCheckPointSelected.current?.(undefined);
                f.setProperties({ SELECTED: SelectedStatus.Default });
            });
        }
    }
    // reset tracks
    forEachFeatureOfLayer(tracksLayerId, (f: Feature) => f.set("SELECTED", SelectedStatus.Default));
    // reset object features
    for (const feature of allFeaturesRef.current) {
        if (feature.get("SELECTED") !== SelectedStatus.Default) {
            feature.set("SELECTED", SelectedStatus.Default);
            onUnselect.current?.(feature.get("OBJECTID"));
        }
        feature.get("ROUTE") && feature.set("ROUTE", 0);
    }
    // reset selected route features, put them back in cluster layer
    const selectedRouteFeatures = selectedSourceRef.current.getFeatures();
    for (const feature of selectedRouteFeatures) {
        feature.set("ROUTE", 0);
        // put back only if PlaceSubtype is not filtered out
        const visibleSubtypes =
            filterOptionsRef.current && filterOptionsRef.current.PLACESUBTYPE?.length
                ? [].concat(...filterOptionsRef.current.PLACESUBTYPE.map((st) => st.value))
                : [];
        (!visibleSubtypes.length || visibleSubtypes.includes(feature.get("PLACESUBTYPE"))) &&
            sourceRef.current.addFeature(feature);
    }
    selectedSourceRef.current.clear();
};

const setRouteTrackSelected = (routeId: string, routeFeature: Feature) => {
    const trackExtent = createEmpty();
    getLayerById(RouteLayers.RouteTrack)
        .getSource()
        .forEachFeature((feature: Feature) => {
            if (feature.get("ROUTEID") === routeId) {
                feature.set("SELECTED", SelectedStatus.Selected);
                feature.setStyle(getRouteSymbol(RouteLayers.RouteTrack, SelectedStatus.Selected));
                extend(trackExtent, feature.getGeometry().getExtent());
            } else {
                feature.set("SELECTED", SelectedStatus.Default);
            }
        });
    zoomToExtent(trackExtent, () => routeFeature.changed());
    showTrackPoints(routeId);
    showRouteObjects(routeId);
};

const showRouteObjects = (routeId: string) => {
    const pointIds = [];
    getLayerById(RouteLayers.RoutePoints)
        .getSource()
        .forEachFeature((feature: Feature) => {
            feature.get("ROUTEID") === routeId &&
                feature.get("TYPE") === RoutePointDataSource.LVM &&
                feature.get("SOURCEID") &&
                pointIds.push(feature.get("SOURCEID"));
        });
    if (pointIds.length) {
        for (const id of pointIds) {
            const feature = allFeaturesRef.current.find((f) => f.get("OBJECTID") === Number(id));
            if (feature) {
                feature.set("ROUTE", 1);
                sourceRef.current.hasFeature(feature) && sourceRef.current.removeFeature(feature);
                selectedSourceRef.current.addFeature(feature);
            }
        }
    }
};
